import { UIButton, UIDiv, UIText } from '@ui';

const TAB_DEFAULT_STYLE =
  'flex items-center gap-2 px-3 py-1 rounded cursor-pointer hover:bg-gray-100 relative';

const STATUS_STYLES_BASE =
  'before:absolute before:top-0.5 before:left-0.5 before:w-2 before:h-2 before:rounded-full';

const STATUS_STYLES = {
  idle: '',
  loading: `${STATUS_STYLES_BASE} before:bg-gray-400 before:animate-pulse`,
  success: `${STATUS_STYLES_BASE} before:bg-[var(--o-hive-primary-100)]`,
  error: `${STATUS_STYLES_BASE} before:bg-red-500`,
};

class AiAnalyzerChatTabs extends UIDiv {
  constructor(onNewChat, onSwitchChat, onCloseChat) {
    super();
    this.setClass(
      'flex ml-1 items-center gap-2 pr-4 py-2 overflow-x-auto overflow-y-none scrollbar-gutter-stable',
    );

    this.tabs = [];
    this.activeTabId = null;
    this.onNewChat = onNewChat;
    this.onSwitchChat = onSwitchChat;
    this.onCloseChat = onCloseChat;
    this.tabsContainer = new UIDiv();
    this.tabsContainer.setClass('flex items-center gap-2');
    this.add(this.tabsContainer);

    // Add new chat button
    const newChatButton = new UIButton('+');
    newChatButton.setClass(
      'p-1.5 rounded-full border border-black hover:bg-gray-100 w-6 h-6 flex items-center justify-center',
    );
    newChatButton.onClick(() => this.onNewChat());
    this.add(newChatButton);
  }

  addTab(id, title = i18next.t('sidebar.aiAnalyzer.new_chat')) {
    const tab = new UIDiv();
    tab.setClass(`${TAB_DEFAULT_STYLE} tab-session-${id}`);
    const tabText = new UIText(title);
    tabText.setClass(`truncate max-w-[120px] tab-session-title-${id}`);
    tab.add(tabText);

    const closeButton = new UIButton('×');
    closeButton.setClass('hover:text-red-500 text-xl');
    closeButton.onClick(e => {
      e.stopPropagation();
      this.#removeTab(id);
    });
    tab.add(closeButton);

    tab.onClick(() => this.setActiveTab(id));

    this.tabs.push({
      id,
      element: tab,
      status: 'idle',
    });
    this.tabsContainer.add(tab);
    this.setActiveTab(id);
  }

  setTabStatus(id, status) {
    const tab = this.tabs.find(tab => tab.id === id);
    if (tab) {
      tab.status = status;
      this.#updateTabStyles();
    }
  }

  setActiveTab(id) {
    this.activeTabId = id;
    this.#updateTabStyles();
    this.onSwitchChat(id);
    setTimeout(() => {
      this.setTabStatus(id, 'idle');
    }, 2000);
  }

  updateTabTitle(id, title) {
    const tab = this.tabs.find(tab => tab.id === id);
    if (!tab) return;
    const tabTextElement = tab.element.find(`.tab-session-title-${id}`)[0];
    tabTextElement.setTextContent(title);
  }

  #removeTab(id) {
    const tab = this.tabs.find(tab => tab.id === id);
    if (tab) {
      tab.element.remove();
      const index = this.tabs.indexOf(tab);
      this.tabs.splice(index, 1);

      if (this.activeTabId === id) {
        // Switch to the previous tab or the first available tab
        const newActiveTab = this.tabs[Math.max(0, index - 1)];
        if (newActiveTab) {
          this.setActiveTab(newActiveTab.id);
        }
      }

      // if the tab is the last one. make a new default tab
      if (this.tabs.length === 0) {
        this.onNewChat();
      }

      this.onCloseChat(id);
      this.#updateTabStyles();
    }
  }

  #updateTabStyles() {
    this.tabs.forEach(tab => {
      const isActive = tab.id === this.activeTabId;
      const statusStyle = STATUS_STYLES[tab.status] || '';
      const baseStyle = isActive
        ? `${TAB_DEFAULT_STYLE} bg-gray-100`
        : TAB_DEFAULT_STYLE;
      tab.element.setClass(`${baseStyle} ${statusStyle} tab-session-${tab.id}`);
    });
  }
}

export { AiAnalyzerChatTabs };
